import { Chip, SxProps } from "@mui/material";
import { WasteClassification, WasteKind } from "generated-graphql/graphql";

import { prettyPrintWasteStreamLabel } from "./Facilities/Facility/prettyPrintWasteClassification";

// Semantic, low-contrast outlined chips per the design system. Severity rises
// neutral → info → warning → error so the classification reads at a glance.
const CLASSIFICATION_COLOR: Record<WasteClassification, "default" | "info" | "warning" | "error"> =
  {
    [WasteClassification.Nonhazardous]: "default",
    [WasteClassification.StateRegulated]: "info",
    [WasteClassification.RcraHazardous]: "warning",
    [WasteClassification.RcraAcutelyHazardous]: "error",
  };

interface WasteClassificationChipProps {
  classification: WasteClassification | null | undefined;
  /**
   * The management program, folded into the same chip. Severity color still
   * comes from the classification alone: the program is not a hazard level.
   */
  wasteKind?: WasteKind | null;
  sx?: SxProps;
}

/**
 * The waste stream's classification and management program as one
 * at-a-glance semantic chip.
 */
export function WasteClassificationChip({
  classification,
  wasteKind,
  sx,
}: WasteClassificationChipProps) {
  const label = prettyPrintWasteStreamLabel(classification, wasteKind);
  if (!label) return null;
  return (
    <Chip
      label={label}
      // A combined label runs long enough to clip in a narrow grid cell, and a
      // clipped chip drops either the program or the hazard class. The native
      // tooltip makes the whole label reachable without widening the column.
      title={label}
      size="small"
      variant="outlined"
      color={classification ? CLASSIFICATION_COLOR[classification] : "default"}
      sx={sx}
    />
  );
}
